// bundles/livcg/panels/src/match-day/deck/widgets.tsx
// The inner content of Deck tiles. Each widget shows more/less content
// depending on its w×h prop (the spec §3 table).

import type { CatalogTeamBlock } from "@livcg/schema";
import { coachSide, matchPhaseSchema } from "@livcg/schema";
import { EyeOff, Minus, Pause, Play, Plus, PowerOff } from "lucide-react";
import { useLiveClock } from "../../shared/liveClock.js";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button, cn, HoldButton, StatusDot } from "../../ui/index.js";
import { rosterModeHasData } from "../control-room/overlayData.js";
import { useGoalFlash } from "../overlayReturn.js";
import type { ChainPlayer } from "../control-room/useQuickEventChain.js";
import { OVERLAY_GROUPS, overlayModeLabel } from "../overlayModes.js";
import { PHASE_LABEL, PHASES } from "../phases.js";
import { byNumber } from "../rosterOrder.js";
import { useMatchDetail } from "../useMatchDetail.js";
import { extraBadgeTitle, useStagedExtraTime } from "../useStagedExtraTime.js";
import { VarControls } from "../VarControls.js";
import type { DeckWidgetProps } from "./registry.js";

const pad = (n: number) => String(n).padStart(2, "0");

export function ClockWidget({ slug, w, h }: DeckWidgetProps) {
  const m = useLiveClock(slug);
  const big = w >= 3 && h >= 2;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-2">
      <div className="font-mono text-3xl font-bold tabular-nums text-ink">
        {pad(m.minute)}:{pad(m.second)}
        {m.additionalTime > 0 && (
          <span className="ml-1 text-lg text-live">+{m.additionalTime}</span>
        )}
      </div>
      <Button
        variant={m.running ? "warning" : "success"}
        size="lg"
        className="w-full"
        onClick={() => void sendRaw("clock.toggle", { slug })}
        leadingIcon={m.running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      >
        {m.running ? "Pauza" : "Start"}
      </Button>
      {big && (
        <div className="flex w-full items-center gap-1.5">
          {[-10, -5, -1, 1, 5, 10].map((d) => (
            <Button
              key={d}
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => void sendRaw("clock.adjust", { slug, deltaSeconds: d })}
            >
              {d > 0 ? `+${d}` : d}
            </Button>
          ))}
        </div>
      )}
      {big && (
        <select
          value={m.phase}
          onChange={(e) => {
            const parsed = matchPhaseSchema.safeParse(e.target.value);
            if (parsed.success) void sendRaw("clock.setPhase", { slug, phase: parsed.data });
          }}
          className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-[13px] text-ink"
        >
          {PHASES.map((phase) => (
            <option key={phase} value={phase}>
              {PHASE_LABEL[phase]}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export function ScoreWidget({ slug, state, w, h }: DeckWidgetProps) {
  const big = w >= 4 && h >= 2;
  // Same as ScoreControl: a manual + also flashes the GOL banner.
  const { flash } = useGoalFlash(slug);
  const side = (s: "home" | "away") => {
    const team = state.teams[s];
    const score = s === "home" ? state.match.homeScore : state.match.awayScore;
    return (
      <div className="flex flex-1 flex-col items-center gap-1.5 min-w-0">
        {big && (
          <span className="flex max-w-full items-center gap-1.5">
            {team?.logoUrl && (
              <img src={team.logoUrl} alt="" className="h-6 w-6 shrink-0 object-contain" />
            )}
            <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
              {team?.shortName || (s === "home" ? "Home" : "Away")}
            </span>
          </span>
        )}
        <div className="flex items-center gap-2">
          <HoldButton
            onConfirm={() => void sendRaw("score.adjust", { slug, side: s, delta: -1 })}
            title="Bosib turing (−1)"
            className="grid h-11 w-11 place-items-center rounded-md border border-border bg-surface text-ink-muted"
          >
            <Minus className="h-4 w-4" />
          </HoldButton>
          <span className="w-10 text-center font-mono text-3xl font-bold tabular-nums text-ink">
            {score}
          </span>
          <button
            type="button"
            onClick={() => {
              void sendRaw("score.adjust", { slug, side: s, delta: 1 });
              flash(s);
            }}
            className="grid h-11 w-11 place-items-center rounded-md border border-ready/50 bg-ready-strong/20 text-ink"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };
  return (
    <div className="flex h-full items-center justify-center gap-3 p-2">
      {side("home")}
      <span className="text-lg font-bold text-ink-faint">:</span>
      {side("away")}
    </div>
  );
}

/** Mode id → group name (per OVERLAY_GROUPS). We do not use
 *  `widgetDef(...)?.group` from registry.tsx — registry imports widgets.js,
 *  and the reverse import would create a CYCLE. Hence a direct local lookup
 *  in OVERLAY_GROUPS. */
function overlayGroupName(modeId: string): string | null {
  for (const group of OVERLAY_GROUPS) {
    if (group.modes.some((m) => m.id === modeId)) return group.name;
  }
  return null;
}

/** The overlay:<modeId> tile — the deck version of the OverlayBoard tile.
 *  Toggle semantics go through services.overlay (useOverlaySwitch) — the SKORE
 *  rules live in a single source. */
export function OverlayTileWidget(p: DeckWidgetProps) {
  // mode is derived from the tile's widget id ("overlay:<modeId>") — DeckGrid
  // (Task 8) always passes widgetId.
  const modeId = p.widgetId.slice("overlay:".length);
  const { isActive, busyMode, toggle } = p.services.overlay;
  const active = isActive(modeId);
  const noData = !active && !rosterModeHasData(modeId, p.state, p.services.extras);
  const disabled = (busyMode !== null && busyMode !== modeId) || noData;
  // spec §3: "2×1+: full label + group name".
  const showGroup = p.w >= 2;
  const groupName = showGroup ? overlayGroupName(modeId) : null;
  return (
    <button
      data-anchor={`tile:${modeId}`}
      type="button"
      disabled={disabled}
      onClick={() => toggle(modeId)}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border p-1 text-center",
        active ? "border-live bg-live-soft" : "border-border bg-surface",
        disabled && "cursor-not-allowed opacity-45 border-dashed",
      )}
    >
      {groupName && (
        <span className="text-[9px] font-semibold uppercase tracking-wider text-ink-faint">
          {groupName}
        </span>
      )}
      <span className="line-clamp-2 text-[12px] font-semibold text-ink">
        {overlayModeLabel(modeId)}
      </span>
      {active && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-live">
          <StatusDot tone="live" pulse /> EFIRDA
        </span>
      )}
    </button>
  );
}

/** The list to display: the matchday roster if there is one (starters first,
 *  each by shirt number), otherwise all of the team's players. Same logic as QuickEvents.tsx
 *  `displayList` — a deck-local copy (it is not imported,
 *  it is component-local). */
function deckDisplayList(block: CatalogTeamBlock | undefined): ChainPlayer[] {
  if (!block) return [];
  const roster = block.roster ?? [];
  if (roster.length > 0) {
    return [
      ...roster.filter((r) => r.isStarter).sort(byNumber),
      ...roster.filter((r) => !r.isStarter).sort(byNumber),
    ].map((r) => ({
      id: r.playerId,
      firstName: r.firstName,
      lastName: r.lastName,
      number: r.number,
    }));
  }
  return [...block.players].sort(byNumber);
}

/** events:home / events:away — player list + GOL/SARIQ/QIZIL (QIZIL is hold).
 *  Substitution is NOT in deck v1 (the two-step pick is more convenient in Control Room). */
export function EventsWidget(p: DeckWidgetProps) {
  const side = (p.widgetId.split(":")[1] ?? "home") as "home" | "away";
  const md = useMatchDetail(p.state);
  // The single instance at the DeckView level (registry.tsx DeckServices.chain) —
  // shared between the home/away tiles, it prevents the timer
  // collision (I1).
  const chain = p.services.chain;
  const block = side === "home" ? md.detail?.home : md.detail?.away;
  const players = deckDisplayList(block);
  /** A booked player (not mid-chain) gets the 2nd-yellow button. */
  const secondYellow = (id: string, key: (t: string) => string) =>
    (p.state.match.playerCards?.[id]?.yellows ?? 0) >= 1 &&
    !p.state.match.playerCards?.[id]?.sentOff &&
    chain.activeChain !== key("yellow_card") &&
    chain.activeChain !== key("second_yellow");
  return (
    <div className="flex h-full flex-col gap-1 p-1.5">
      <div className="flex items-center justify-between px-1">
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-muted">
          {side === "home" ? "Home" : "Away"} · {block?.team.shortName ?? ""}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {[...players, ...(block ? [md.coach(side)] : [])].map((pl) => {
          const key = (t: string) => `${t}:${side}:${pl.id}`;
          const isCoach = coachSide(pl.id) !== null;
          // Sent off: only a banner still on air can be taken back off.
          const off = !!p.state.match.playerCards?.[pl.id]?.sentOff;
          const lockedFor = (t: string) => off && chain.activeChain !== key(t);
          return (
            <div key={pl.id} className="flex items-center gap-1 border-b border-divider/50 py-0.5">
              <span className="w-6 shrink-0 text-right font-mono text-[11px] text-ink-faint">
                {isCoach ? "M" : (pl.number ?? "")}
              </span>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-[12px] text-ink",
                  off && "text-ink-faint line-through",
                )}
              >
                {pl.lastName || pl.firstName}
              </span>
              {!isCoach && (
                <Button
                  variant={chain.activeChain === key("goal") ? "live" : "success"}
                  size="xs"
                  disabled={lockedFor("goal")}
                  onClick={() => block && chain.fire("goal", side, pl, block.team)}
                >
                  G
                </Button>
              )}
              {secondYellow(pl.id, key) ? (
                // Already booked: a second S is the 2nd yellow → red (hold to confirm),
                // as in Control's confirm, not a plain yellow that keeps him on.
                <HoldButton
                  disabled={lockedFor("second_yellow")}
                  onConfirm={() => block && chain.fire("second_yellow", side, pl, block.team)}
                  title="Bosib turing — 2-sariq → QIZIL"
                  className="h-6 rounded-md bg-warning px-1.5 text-[11px] font-semibold text-ink ring-1 ring-live disabled:cursor-not-allowed disabled:opacity-40"
                >
                  2S
                </HoldButton>
              ) : (
                <Button
                  variant={
                    chain.activeChain === key("yellow_card") ||
                    chain.activeChain === key("second_yellow")
                      ? "live"
                      : "warning"
                  }
                  size="xs"
                  disabled={lockedFor("yellow_card") && lockedFor("second_yellow")}
                  onClick={() => {
                    if (!block) return;
                    const t =
                      chain.activeChain === key("second_yellow") ? "second_yellow" : "yellow_card";
                    chain.fire(t, side, pl, block.team);
                  }}
                >
                  S
                </Button>
              )}
              <HoldButton
                disabled={lockedFor("red_card")}
                onConfirm={() => block && chain.fire("red_card", side, pl, block.team)}
                title="Bosib turing — QIZIL"
                className="h-6 rounded-md bg-live px-2 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Q
              </HoldButton>
            </div>
          );
        })}
        {players.length === 0 && (
          <div className="grid h-full place-items-center text-[11px] text-ink-faint">
            Tarkib topilmadi
          </div>
        )}
      </div>
    </div>
  );
}

export function VarWidget({ slug, state }: DeckWidgetProps) {
  return (
    <div className="h-full overflow-y-auto p-2">
      <VarControls slug={slug} state={state} />
    </div>
  );
}

/** Takes the current overlay off air (Esc) — the same return rules as Control. */
export function HideWidget({ services }: DeckWidgetProps) {
  const current = services.overlay.current;
  return (
    <button
      type="button"
      onClick={services.overlay.hideCurrent}
      disabled={!current}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border px-1 text-center",
        current
          ? "border-live/50 bg-live-soft text-live"
          : "cursor-not-allowed border-border text-ink-faint",
      )}
    >
      <EyeOff className="h-4 w-4" />
      <span className="text-[11px] font-semibold uppercase tracking-wider">Yashirish</span>
      {current && (
        <span className="max-w-full truncate text-[10px]">{overlayModeLabel(current)}</span>
      )}
    </button>
  );
}

export function AllOffWidget({ services }: DeckWidgetProps) {
  return (
    <HoldButton
      onConfirm={services.allOff}
      title="Bosib turing — barcha overlay va VAR o'chadi"
      className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-live bg-live-soft text-live"
    >
      <PowerOff className="h-5 w-5" strokeWidth={2.5} />
      <span className="text-[10px] font-bold uppercase tracking-wider">ALL OFF</span>
    </HoldButton>
  );
}

/** The value itself is the "on air" button: −/+ stage it locally, pressing the number
 *  sends it to the replicant (so intermediate values do not go on air). */
export function ExtraTimeWidget({ slug, state }: DeckWidgetProps) {
  const extra = useStagedExtraTime(slug, state);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
        Extra
      </span>
      <div className="flex items-center gap-1">
        <Button variant="secondary" size="xs" disabled={extra.draft <= 0} onClick={extra.dec}>
          <Minus className="h-3 w-3" />
        </Button>
        <button
          type="button"
          onClick={extra.send}
          disabled={!extra.canSend}
          title={extraBadgeTitle(extra.badge, extra.draft)}
          aria-label={extraBadgeTitle(extra.badge, extra.draft)}
          className={cn(
            "w-11 rounded-md py-0.5 text-center font-mono text-lg font-bold tabular-nums transition-colors",
            extra.badge === "staged" &&
              "cursor-pointer bg-warning/25 text-warning ring-2 ring-warning/70 animate-pulse",
            extra.badge === "sent" && "bg-warning/15 text-warning",
            extra.badge === "live" && "bg-live/20 text-live",
            extra.badge === "empty" && "text-ink",
          )}
        >
          +{extra.draft}
        </button>
        <Button variant="secondary" size="xs" onClick={extra.inc}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
      <button
        type="button"
        onClick={() => extra.setOnAir(!extra.onAir)}
        disabled={extra.draft <= 0}
        title="Soat ostidagi strip efirda turadimi (banner chiqmaydi)"
        className={cn(
          "rounded px-1.5 text-[9px] font-semibold uppercase tracking-wider transition-colors",
          extra.draft <= 0 && "opacity-40",
          extra.onAir ? "bg-live/20 text-live" : "text-ink-faint",
        )}
      >
        Strip {extra.onAir ? "ON" : "OFF"}
      </button>
    </div>
  );
}

export function AutoSponsorWidget({ slug, state }: DeckWidgetProps) {
  const on = state.autoSponsor ?? false;
  return (
    <button
      type="button"
      onClick={() => void sendRaw("sponsorAuto.set", { slug, enabled: !on })}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border text-center",
        on ? "border-ready bg-ready-strong/20 text-ink" : "border-border bg-surface text-ink-muted",
      )}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider">Auto sponsor</span>
      <span className={cn("text-[12px] font-bold", on ? "text-ready" : "text-ink-faint")}>
        {on ? "YOQIQ" : "O'CHIQ"}
      </span>
    </button>
  );
}
